import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useDiscoveryOptional } from '../../contexts/discoveryStore';
import { requestControlledReload } from '../../lib/swControl';
import { exposeSsrRoot } from '../../utils/ssrShell';
import styles from './DiscoveryEpochNotice.module.css';

/**
 * Upgrade / maintenance blocking state + invalid/unavailable territory notices
 * (T016-FE, plan l.71/74 and the T011 §9.1 / T012 §5 / T014 §8 handoffs).
 *
 * Mounted by `DiscoveryProvider`, so it covers EVERY public surface — the
 * location popup, profile and creator forms fall back to their legacy lane in
 * `upgrade-required` (they key on `mode === 'v2'`), and this overlay is what
 * makes that safe: plan l.71 forbids reactivating old GPS/generic-localized
 * behaviour in that state, and a full-screen modal block means those lanes are
 * never reachable, with an honest message instead.
 *
 * Three surfaces:
 * - `upgrade-required` (reason `protocol` or `maintenance`): BLOCKING. No
 *   dismiss — the client cannot work against an incompatible/unreachable
 *   epoch, and silently continuing would mean legacy behaviour. "Aggiorna"
 *   runs the controlled reload (activate waiting worker + purge public
 *   snapshots + reload through the plugin handle), never a bare
 *   `location.reload()`. `retry()` (Riprova) re-runs the bounded
 *   renegotiation for the maintenance case.
 * - v2 `status: 'unavailable'`: non-blocking alert + Riprova (the feed is
 *   already held by the bridge; this is the honest error the plan asks for
 *   instead of showing a previous territory under the failed URL).
 * - v2 `status: 'invalid'`: non-blocking alert with a link home — the URL's
 *   filters are unrecoverable, so the only honest action is leaving them,
 *   never broadening the query in place (plan l.60).
 *
 * Nothing renders in `legacy`/`loading` — pre-activation parity.
 */
export function DiscoveryEpochNotice() {
  const discovery = useDiscoveryOptional();
  const reloadButtonRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const upgradeRequired = discovery?.mode === 'upgrade-required';
  const invalid = discovery?.mode === 'v2' && discovery.status === 'invalid';
  const unavailable = discovery?.mode === 'v2' && discovery.status === 'unavailable';
  const retry = discovery?.retry;

  // T102-INT: on a server-rendered page the root is still invisible and
  // `inert` behind the shell. These three states cannot resolve by themselves
  // and carry the user's only way out (Aggiorna / Riprova): show them.
  const needsVisibleNotice = upgradeRequired || invalid || unavailable;
  useEffect(() => {
    if (needsVisibleNotice) exposeSsrRoot();
  }, [needsVisibleNotice]);

  // Blocking state: capture the outgoing focus and move it onto the reload
  // action; restore on unmount so closing the state does not strand focus.
  useEffect(() => {
    if (!upgradeRequired) return undefined;
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    reloadButtonRef.current?.focus();
    return () => {
      const restore = restoreFocusRef.current;
      if (restore && restore.isConnected) restore.focus();
    };
  }, [upgradeRequired]);

  if (!discovery) return null;

  // Shared shell for the two non-blocking territory notices.
  const notice = (message: string, action: ReactNode) => (
    <div className={styles.notice} role="alert">
      <p className={styles.noticeText}>{message}</p>
      {action}
    </div>
  );

  if (upgradeRequired) {
    const maintenance = discovery.upgradeReason === 'maintenance';
    // One copy block per reason — title, body and primary action always agree.
    const copy = maintenance
      ? {
          title: 'Servizio temporaneamente non disponibile',
          body: 'Non riusciamo a verificare la copertura dei territori. Riprova tra qualche istante: i tuoi filtri e le tue preferenze restano salvati.',
          action: 'Riprova di nuovo',
        }
      : {
          title: 'Aggiornamento richiesto',
          body: 'Questa versione dell’app non è più compatibile con i dati aggiornati. Aggiorna per continuare con l’ultima versione.',
          action: 'Aggiorna ora',
        };
    const swallowEscape = (event: KeyboardEvent) => {
      // The block cannot be dismissed with a keypress — there is no valid
      // state to dismiss it TO (plan l.71: never legacy).
      if (event.key === 'Escape') event.preventDefault();
    };
    return (
      <div className={styles.backdrop} onKeyDown={swallowEscape}>
        <div
          className={styles.dialog}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="discovery-upgrade-title"
          aria-describedby="discovery-upgrade-body"
        >
          <h2 id="discovery-upgrade-title" className={styles.title}>
            {copy.title}
          </h2>
          <p id="discovery-upgrade-body" className={styles.description}>
            {copy.body}
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              ref={reloadButtonRef}
              className={styles.primaryAction}
              onClick={() => {
                void requestControlledReload();
              }}
            >
              {copy.action}
            </button>
            {maintenance && retry ? (
              <button type="button" className={styles.secondaryAction} onClick={retry}>
                Controlla connessione
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (invalid) {
    return notice(
      'Questi filtri non sono validi: controlla le date, la distanza o la ricerca.',
      <Link className={styles.noticeAction} to="/">
        Torna agli eventi
      </Link>
    );
  }

  if (unavailable) {
    return notice(
      'Non riusciamo a caricare questo territorio. Controlla la connessione e riprova.',
      retry ? (
        <button type="button" className={styles.noticeAction} onClick={retry}>
          Riprova
        </button>
      ) : null
    );
  }

  return null;
}
